/** What a document's row says beyond its stage: why it was refused and how to get out of it, and that
 * its retention has run out.
 *
 * **The reason and the remedy are two elements** (DOC-01): "send it again", "export it differently"
 * and "remove the password" are three different actions, and the remedy is derived on the server from
 * `refusal_code`, so a better sentence written today applies to a document refused last year.
 *
 * **A refusal with a reason and no way out is the dead end DOC-01 exists to prevent, and it is
 * reachable**: a code that table does not know arrives with `refusal_remedy: null`. Saying nothing
 * would read as "no next step needed"; saying so, with the code, lets someone act. The sentence is
 * deliberately not a remedy of our own invention.
 *
 * **Past its retention date** (DOC-06): `delete_after` has been on the row since the first migration,
 * and evidence EmitIQ has undertaken to destroy must not look exactly like evidence it holds, which
 * invites someone to file a figure on it.
 */

import type { DocumentRow } from "../../api/evidence";
import styles from "./DocumentNote.module.css";

/** The note as one piece of text: the cell's title (the cell is one line) and the export's value. Empty when
 * the document has no note. */
export function noteText(document: DocumentRow): string {
  const refused = document.state === "quarantined";
  return [
    document.state_reason,
    document.refusal_remedy ?? (refused ? `No recorded next step${document.refusal_code ? ` (code ${document.refusal_code})` : ""}.` : null),
    document.retention_expired ? `Retention expired ${document.delete_after}. Do not rely on it for a new figure.` : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" ");
}

export function DocumentNote({ document }: { document: DocumentRow }) {
  const refused = document.state === "quarantined";
  if (!(document.state_reason || document.refusal_remedy || refused || document.retention_expired)) {
    return null;
  }
  return (
    <span className={styles.note} data-refusal-code={document.refusal_code ?? undefined}>
      {document.state_reason ? (
        <span className={styles.reason} data-testid="refusal-reason">
          {document.state_reason}
        </span>
      ) : null}
      {document.refusal_remedy ? (
        <span className={styles.remedy} data-testid="refusal-remedy">
          {document.refusal_remedy}
        </span>
      ) : refused ? (
        <span className={styles.remedy} data-testid="refusal-no-remedy">
          No recorded next step{document.refusal_code ? ` (code ${document.refusal_code})` : ""}.
        </span>
      ) : null}
      {document.retention_expired ? (
        <span className={styles.reason} data-testid="retention-expired">
          Retention expired {document.delete_after}. Do not rely on it for a new figure.
        </span>
      ) : null}
    </span>
  );
}
